/**
 * @fileoverview Cost Management admin view (MOR-735)
 *
 * Owner-only billing dashboard over the vendor cost ledger
 * (convex/admin/costManagement.ts):
 * - Period filter (day / week / month / quarter / 90 days / all time)
 * - Platform stat cards (cost, revenue, margin, commission, jobs)
 * - Daily cost chart stacked by vendor
 * - Per-organisation table with affiliate rev-share toggle + margin estimates
 * - Org drill-down (per-job costs + mean stage breakdown)
 * - Job drill-down (per-stage × vendor costs)
 *
 * Money display: vendor costs are stored in micro-USD and converted to AUD
 * for margin maths server-side; this view shows costs in USD (what we pay)
 * and revenue/margin figures in AUD (what we bill).
 *
 * NEVER link this view (or its queries) anywhere a firm user could reach.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { CostVendor } from "@cvx/costs/costEvents/schema";
import type { FunctionReturnType } from "convex/server";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Switch } from "@/ui/primitives/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { toast } from "sonner";
import { ChevronRight, Loader2, X } from "lucide-react";

// ---------------------------------------------------------------------------
// Vendors — fixed order & colors (never reassigned when a vendor is absent)
// ---------------------------------------------------------------------------

const VENDORS = [
  "anthropic",
  "openai",
  "reducto",
  "infotrack",
  "bedrock",
] as const satisfies readonly CostVendor[];
type Vendor = CostVendor;

// Typed off the schema's union, so a vendor added there fails typecheck here
// until it has a label and a chart colour.
const VENDOR_LABELS: Record<Vendor, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  reducto: "Reducto",
  infotrack: "InfoTrack",
  bedrock: "AWS Bedrock",
};

const chartConfig = {
  anthropic: { label: "Anthropic", color: "hsl(var(--chart-1))" },
  openai: { label: "OpenAI", color: "hsl(var(--chart-2))" },
  reducto: { label: "Reducto", color: "hsl(var(--chart-3))" },
  infotrack: { label: "InfoTrack", color: "hsl(var(--chart-4))" },
  bedrock: { label: "AWS Bedrock", color: "hsl(var(--chart-5))" },
} satisfies ChartConfig & Record<Vendor, unknown>;

// ---------------------------------------------------------------------------
// Period presets
// ---------------------------------------------------------------------------

type PeriodKey = "today" | "week" | "month" | "quarter" | "90d" | "all";

const PERIODS: Array<{ key: PeriodKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "week", label: "Last 7 days" },
  { key: "month", label: "This month" },
  { key: "quarter", label: "This quarter" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
];

/** UTC `YYYY-MM-DD` for a timestamp — must match the ledger's day axis. */
function utcDay(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

function periodRange(key: PeriodKey): { startDay: string; endDay: string } {
  const now = new Date();
  const endDay = utcDay(now.getTime());
  const DAY_MS = 24 * 60 * 60 * 1000;
  switch (key) {
    case "today":
      return { startDay: endDay, endDay };
    case "week":
      return { startDay: utcDay(now.getTime() - 6 * DAY_MS), endDay };
    case "month": {
      const start = `${endDay.slice(0, 7)}-01`;
      return { startDay: start, endDay };
    }
    case "quarter": {
      const month = now.getUTCMonth();
      const qStartMonth = month - (month % 3);
      const start = `${now.getUTCFullYear()}-${String(qStartMonth + 1).padStart(2, "0")}-01`;
      return { startDay: start, endDay };
    }
    case "90d":
      return { startDay: utcDay(now.getTime() - 89 * DAY_MS), endDay };
    case "all":
      // No cost events predate 2025 — the ledger shipped mid-2026.
      return { startDay: "2025-01-01", endDay };
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function formatUsdFromMicros(micros: number): string {
  const usd = micros / 1_000_000;
  if (usd > 0 && usd < 0.01) return "<$0.01";
  return usd.toLocaleString("en-AU", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function formatAudFromCents(cents: number): string {
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 2,
  });
}

function formatCount(n: number): string {
  return n.toLocaleString("en-AU");
}

const STAGE_GROUP_LABELS: Record<string, string> = {
  chat: "Chat",
  gather_information: "Gather Information",
  summarization: "Summarisation Agents",
  reporting: "Reporting",
  searches: "Searches",
  contacts: "Contacts",
  mail: "Mail",
  recoveries: "Recoveries",
  job_setup: "Job Setup",
  settings: "Settings & Templates",
};

/** "summarization.sum_asset_list" → "Summarisation Agents · Asset List" */
function stageLabel(stage: string): string {
  const dot = stage.indexOf(".");
  const group = dot === -1 ? stage : stage.slice(0, dot);
  const detail = dot === -1 ? "" : stage.slice(dot + 1);
  const groupLabel = STAGE_GROUP_LABELS[group] ?? group;
  if (!detail) return groupLabel;
  const detailLabel = detail
    .replace(/^sum_/, "")
    .replace(/^flagged/, "flagged ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${groupLabel} · ${detailLabel}`;
}

interface VendorTotals {
  vendor: Vendor;
  costUsdMicros: number;
  searchFeeAudCents: number;
  commissionAudCents: number;
  eventCount: number;
}

function vendorSummary(byVendor: VendorTotals[]): string {
  return byVendor
    .filter((v) => v.costUsdMicros > 0)
    .map(
      (v) =>
        `${VENDOR_LABELS[v.vendor]} ${formatUsdFromMicros(v.costUsdMicros)}`,
    )
    .join(" · ");
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function CostManagementView() {
  const [period, setPeriod] = useState<PeriodKey>("month");
  const [selectedOrgId, setSelectedOrgId] =
    useState<Id<"organizations"> | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<Id<"jobs"> | null>(null);

  const range = useMemo(() => periodRange(period), [period]);

  const { data, isPending } = useQuery(
    convexQuery(api.admin.costManagement.getPlatformCostOverview, range),
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Period filter */}
      <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border bg-card p-1">
        {PERIODS.map((p) => (
          <Button
            key={p.key}
            variant={period === p.key ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "h-7 px-3 text-xs",
              period === p.key ? "font-medium" : "text-muted-foreground",
            )}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {isPending || !data ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading cost data…
        </div>
      ) : (
        <>
          <StatCards data={data} />
          <DailyCostChart byDay={data.byDay} />
          <OrgsTable
            data={data}
            selectedOrgId={selectedOrgId}
            onSelectOrg={(orgId) => {
              setSelectedOrgId(orgId === selectedOrgId ? null : orgId);
              setSelectedJobId(null);
            }}
          />
          {selectedOrgId && (
            <OrgBreakdownPanel
              orgId={selectedOrgId}
              range={range}
              selectedJobId={selectedJobId}
              onSelectJob={(jobId) =>
                setSelectedJobId(jobId === selectedJobId ? null : jobId)
              }
              onClose={() => {
                setSelectedOrgId(null);
                setSelectedJobId(null);
              }}
            />
          )}
          {selectedJobId && (
            <JobBreakdownPanel
              jobId={selectedJobId}
              onClose={() => setSelectedJobId(null)}
            />
          )}
          <AssumptionsFootnote assumptions={data.assumptions} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat cards
// ---------------------------------------------------------------------------

type PlatformOverview = FunctionReturnType<
  typeof api.admin.costManagement.getPlatformCostOverview
>;

function StatCard(props: {
  label: string;
  value: string;
  sub?: string;
  valueClassName?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {props.label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn("text-2xl font-semibold", props.valueClassName)}>
          {props.value}
        </div>
        {props.sub && (
          <p className="mt-1 text-xs text-muted-foreground">{props.sub}</p>
        )}
      </CardContent>
    </Card>
  );
}

function StatCards({ data }: { data: PlatformOverview }) {
  const e = data.platformEconomics;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
      <StatCard
        label="Vendor costs"
        value={formatUsdFromMicros(data.totals.costUsdMicros)}
        sub={vendorSummary(data.byVendor as VendorTotals[]) || "No spend yet"}
      />
      <StatCard
        label="Est. revenue"
        value={formatAudFromCents(e.revenueAudCents)}
        sub={`${formatCount(data.totals.activeJobCount)} active job${data.totals.activeJobCount === 1 ? "" : "s"} × $2,000`}
      />
      <StatCard
        label="Est. margin"
        value={formatAudFromCents(e.marginAudCents)}
        sub={
          e.marginPercent !== null
            ? `${e.marginPercent}% of revenue`
            : undefined
        }
        valueClassName={e.marginAudCents < 0 ? "text-destructive" : undefined}
      />
      <StatCard
        label="Affiliate share"
        value={formatAudFromCents(e.affiliateShareAudCents)}
        sub="Rev-share owed to affiliates"
      />
      <StatCard
        label="InfoTrack commission"
        value={formatAudFromCents(data.totals.commissionAudCents)}
        sub={
          data.totals.searchFeeAudCents > 0
            ? `on ${formatAudFromCents(data.totals.searchFeeAudCents)} of searches`
            : "No search fees recorded"
        }
      />
      <StatCard
        label="Avg cost / job"
        value={
          data.totals.activeJobCount > 0
            ? formatUsdFromMicros(
                Math.round(
                  data.totals.costUsdMicros / data.totals.activeJobCount,
                ),
              )
            : "—"
        }
        sub="Mean vendor cost per active job"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily cost chart
// ---------------------------------------------------------------------------

function DailyCostChart({ byDay }: { byDay: PlatformOverview["byDay"] }) {
  const data = byDay.map((d) => ({
    day: d.day.slice(5), // MM-DD
    anthropic: (d.costUsdMicrosByVendor.anthropic ?? 0) / 1_000_000,
    openai: (d.costUsdMicrosByVendor.openai ?? 0) / 1_000_000,
    reducto: (d.costUsdMicrosByVendor.reducto ?? 0) / 1_000_000,
    infotrack: (d.costUsdMicrosByVendor.infotrack ?? 0) / 1_000_000,
    bedrock: (d.costUsdMicrosByVendor.bedrock ?? 0) / 1_000_000,
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          Daily vendor cost (USD)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No cost events in this period.
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="h-[240px] w-full">
            <BarChart data={data}>
              <CartesianGrid
                vertical={false}
                stroke="hsl(var(--border))"
                strokeOpacity={0.5}
              />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={(v: number) => `$${v.toFixed(v < 10 ? 2 : 0)}`}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
                width={64}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(v) =>
                      typeof v === "number" ? `$${v.toFixed(4)}` : String(v)
                    }
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              {VENDORS.map((vendor) => (
                <Bar
                  key={vendor}
                  dataKey={vendor}
                  stackId="cost"
                  fill={`var(--color-${vendor})`}
                  stroke="hsl(var(--background))"
                  strokeWidth={1}
                  maxBarSize={32}
                />
              ))}
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Organisations table (with affiliate toggle)
// ---------------------------------------------------------------------------

function OrgsTable(props: {
  data: PlatformOverview;
  selectedOrgId: Id<"organizations"> | null;
  onSelectOrg: (orgId: Id<"organizations">) => void;
}) {
  const { data, selectedOrgId, onSelectOrg } = props;

  const { mutateAsync: setAffiliate } = useMutation({
    mutationFn: useConvexMutation(
      api.admin.costManagement.setAffiliateRevShare,
    ),
  });

  const handleToggleAffiliate = async (
    orgId: Id<"organizations">,
    enabled: boolean,
  ) => {
    try {
      await setAffiliate({ orgId, enabled });
      toast.success(
        enabled
          ? "Affiliate rev-share enabled"
          : "Affiliate rev-share disabled",
      );
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Failed to update affiliate setting"),
      );
    }
  };

  const handlePercentCommit = async (
    orgId: Id<"organizations">,
    raw: string,
  ) => {
    const percent = Number(raw);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      toast.error("Rev-share percent must be between 0 and 100");
      return;
    }
    try {
      await setAffiliate({ orgId, enabled: true, percent });
      toast.success(`Affiliate share set to ${percent}%`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update affiliate share"));
    }
  };

  return (
    <div className="rounded-lg border">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-medium">Organisations</h2>
        <p className="text-xs text-muted-foreground">
          Costs, margins and affiliate rev-share for the selected period. Click
          a row for the per-job breakdown.
        </p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organisation</TableHead>
            <TableHead className="text-right">Jobs</TableHead>
            <TableHead className="text-right">Vendor cost</TableHead>
            <TableHead className="text-right">Avg / job</TableHead>
            <TableHead className="text-right">Commission</TableHead>
            <TableHead className="text-right">Est. revenue</TableHead>
            <TableHead className="text-right">Est. margin</TableHead>
            <TableHead>Affiliate</TableHead>
            <TableHead className="w-[48px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.perOrg.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={9}
                className="py-8 text-center text-muted-foreground"
              >
                No cost activity recorded in this period.
              </TableCell>
            </TableRow>
          ) : (
            data.perOrg.map((org) => (
              <TableRow
                key={org.orgId}
                className={cn(
                  "cursor-pointer",
                  selectedOrgId === org.orgId && "bg-muted/50",
                )}
                onClick={() => onSelectOrg(org.orgId)}
              >
                <TableCell className="font-medium">{org.orgName}</TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatCount(org.activeJobCount)}
                </TableCell>
                <TableCell className="text-right">
                  {formatUsdFromMicros(org.costUsdMicros)}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {org.avgCostPerJobUsdMicros !== null
                    ? formatUsdFromMicros(org.avgCostPerJobUsdMicros)
                    : "—"}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatAudFromCents(org.economics.commissionAudCents)}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatAudFromCents(org.economics.revenueAudCents)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-medium",
                    org.economics.marginAudCents < 0 && "text-destructive",
                  )}
                >
                  {formatAudFromCents(org.economics.marginAudCents)}
                  {org.economics.marginPercent !== null && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({org.economics.marginPercent}%)
                    </span>
                  )}
                </TableCell>
                <TableCell
                  onClick={(e) => e.stopPropagation()}
                  className="cursor-default"
                >
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={org.affiliateRevShareEnabled}
                      onCheckedChange={(checked) =>
                        handleToggleAffiliate(org.orgId, checked)
                      }
                      aria-label="Toggle affiliate rev-share"
                    />
                    {org.affiliateRevShareEnabled && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          defaultValue={org.affiliateRevSharePercent}
                          className="h-7 w-16 text-right text-xs"
                          aria-label="Affiliate rev-share percent"
                          onBlur={(e) => {
                            if (
                              Number(e.target.value) !==
                              org.affiliateRevSharePercent
                            ) {
                              handlePercentCommit(org.orgId, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform",
                      selectedOrgId === org.orgId && "rotate-90",
                    )}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Org drill-down
// ---------------------------------------------------------------------------

function OrgBreakdownPanel(props: {
  orgId: Id<"organizations">;
  range: { startDay: string; endDay: string };
  selectedJobId: Id<"jobs"> | null;
  onSelectJob: (jobId: Id<"jobs">) => void;
  onClose: () => void;
}) {
  const { data, isPending } = useQuery(
    convexQuery(api.admin.costManagement.getOrgCostBreakdown, {
      orgId: props.orgId,
      ...props.range,
    }),
  );

  return (
    <div className="rounded-lg border">
      <div className="flex items-start justify-between border-b px-4 py-3">
        <div>
          <h2 className="text-sm font-medium">
            {data ? data.orgName : "Organisation"} — job breakdown
          </h2>
          <p className="text-xs text-muted-foreground">
            Jobs with cost activity in the selected period. Click a job for its
            stage breakdown.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={props.onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isPending || !data ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="flex flex-col gap-6 p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead className="text-right">Vendor cost</TableHead>
                <TableHead>By vendor</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">Est. margin</TableHead>
                <TableHead className="w-[48px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.orgLevel.costUsdMicros > 0 && (
                <TableRow className="bg-muted/30">
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        Firm-level platform usage
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Workspace &amp; contact chats, job setup, template
                        parsing, task imports
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatUsdFromMicros(data.orgLevel.costUsdMicros)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {vendorSummary(data.orgLevel.byVendor as VendorTotals[]) ||
                      "—"}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    —
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    —
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
              {data.perJob.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-6 text-center text-muted-foreground"
                  >
                    No job-attributed costs in this period.
                  </TableCell>
                </TableRow>
              ) : (
                data.perJob.map((job) => (
                  <TableRow
                    key={job.jobId}
                    className={cn(
                      "cursor-pointer",
                      props.selectedJobId === job.jobId && "bg-muted/50",
                    )}
                    onClick={() => props.onSelectJob(job.jobId)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{job.jobLabel}</span>
                        {job.jobCode && (
                          <span className="text-xs text-muted-foreground">
                            {job.jobCode}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatUsdFromMicros(job.costUsdMicros)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {vendorSummary(job.byVendor as VendorTotals[]) || "—"}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatAudFromCents(job.commissionAudCents)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right font-medium",
                        job.economics.marginAudCents < 0 && "text-destructive",
                      )}
                    >
                      {formatAudFromCents(job.economics.marginAudCents)}
                      {job.economics.marginPercent !== null && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({job.economics.marginPercent}%)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ChevronRight
                        className={cn(
                          "h-4 w-4 text-muted-foreground transition-transform",
                          props.selectedJobId === job.jobId && "rotate-90",
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {data.avgStageBreakdown.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium">
                Average job cost by stage
              </h3>
              <p className="mb-3 text-xs text-muted-foreground">
                Mean across the {data.avgStageBreakdown[0]?.jobCount ?? 0} job
                {(data.avgStageBreakdown[0]?.jobCount ?? 0) === 1
                  ? ""
                  : "s"}{" "}
                above, using each job's all-time stage totals.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Avg / job</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.avgStageBreakdown.map((s) => (
                    <TableRow key={`${s.vendor}-${s.stage}`}>
                      <TableCell>{stageLabel(s.stage)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {VENDOR_LABELS[s.vendor as Vendor]}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatUsdFromMicros(s.avgCostUsdMicrosPerJob)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatUsdFromMicros(s.totalCostUsdMicros)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job drill-down
// ---------------------------------------------------------------------------

function JobBreakdownPanel(props: { jobId: Id<"jobs">; onClose: () => void }) {
  const { data, isPending } = useQuery(
    convexQuery(api.admin.costManagement.getJobCostBreakdown, {
      jobId: props.jobId,
    }),
  );

  return (
    <div className="rounded-lg border">
      <div className="flex items-start justify-between border-b px-4 py-3">
        <div>
          <h2 className="text-sm font-medium">
            {data ? data.jobLabel : "Job"} — stage breakdown
            {data?.jobCode && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {data.jobCode}
              </span>
            )}
          </h2>
          <p className="text-xs text-muted-foreground">
            All-time vendor spend attributed to each pipeline stage and agent.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={props.onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isPending || !data ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Total cost: </span>
              <span className="font-medium">
                {formatUsdFromMicros(data.totals.costUsdMicros)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Commission: </span>
              <span className="font-medium">
                {formatAudFromCents(data.totals.commissionAudCents)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Est. margin: </span>
              <span
                className={cn(
                  "font-medium",
                  data.economics.marginAudCents < 0 && "text-destructive",
                )}
              >
                {formatAudFromCents(data.economics.marginAudCents)}
                {data.economics.marginPercent !== null && (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    ({data.economics.marginPercent}%)
                  </span>
                )}
              </span>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stage</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Tokens in / out</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Events</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.stages.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-6 text-center text-muted-foreground"
                  >
                    No cost events attributed to this job yet.
                  </TableCell>
                </TableRow>
              ) : (
                data.stages.map((s) => (
                  <TableRow key={`${s.vendor}-${s.stage}`}>
                    <TableCell>{stageLabel(s.stage)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {VENDOR_LABELS[s.vendor as Vendor]}
                    </TableCell>
                    <TableCell className="text-right">
                      {s.vendor === "infotrack"
                        ? `${formatAudFromCents(s.commissionAudCents)} comm.`
                        : formatUsdFromMicros(s.costUsdMicros)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {s.inputTokens + s.cacheReadTokens + s.cacheWriteTokens >
                      0
                        ? `${formatCount(s.inputTokens + s.cacheReadTokens + s.cacheWriteTokens)} / ${formatCount(s.outputTokens)}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {s.reductoCredits > 0
                        ? formatCount(Math.round(s.reductoCredits))
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCount(s.eventCount)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assumptions footnote
// ---------------------------------------------------------------------------

function AssumptionsFootnote({
  assumptions,
}: {
  assumptions: PlatformOverview["assumptions"];
}) {
  return (
    <p className="text-xs text-muted-foreground">
      Assumptions: revenue{" "}
      {formatAudFromCents(assumptions.assumedJobRevenueAudCents)} per active job
      · USD costs converted at {assumptions.audPerUsd} AUD/USD · InfoTrack
      commission = fee × {assumptions.infotrackShareOfSearch * 100}% ×{" "}
      {assumptions.morganShareOfInfotrackMargin * 100}% · default affiliate
      share {assumptions.defaultAffiliateRevSharePercent}%. Anthropic, OpenAI
      and Reducto figures are computed from recorded usage at current list
      pricing; InfoTrack searches are billed to the firm directly.
    </p>
  );
}
